import React, { useCallback, useMemo, useRef } from "react";
import styles from "./date-picker.module.scss";
import classnames from "classnames";
import globalStyles from "../../globalStyles";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import TextField from "@mui/material/TextField";
import { Svg } from "../Svg";
import omit from "lodash/omit";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { getDateFormatString } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import type { BaseDatePickerProps } from "@whatslly/types/UI-V2/BaseElements/DatePickerType";
export const DatePicker: React.FC<BaseDatePickerProps> = React.memo<BaseDatePickerProps>(
  ({
    paddingInline,
    padding,
    marginBottom,
    marginTop,
    margin,
    className,
    onChange,
    borderColor,
    value,
    marginRight,
    marginLeft,
    format,
    disabled,
    backgroundColor,
    DatePickerContainerClassName,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    onTouched,
    darkMode,
    onInvalidInput,
    name,
    "data-testid": dataTestId,
    onOpenChange,
    hideCalendarIcon,
    openCalendarInTheMiddle,
    maxDate,
    minDate,
  }) => {
    const { i18n } = useTranslation();
    const formatToUse = useMemo(() => format ?? getDateFormatString(i18n.language), [format, i18n.language]);

    const valueToUse = useMemo(() => {
      const date = value ? (!isNaN(Number(value)) ? dayjs.unix(Number(value) / 1000) : dayjs(value, formatToUse)) : null;

      // If the date is outside the allowed range, return null
      return date && date.isAfter(maxDate) && date.isBefore(minDate) ? null : date;
    }, [maxDate, minDate, formatToUse, value]);

    const calendarIconRef = useRef<HTMLDivElement>(null);

    const getOnChangeProps = useCallback(
      (date: Dayjs | null) => {
        const now = dayjs();
        const dateToReturn = !isNaN(Number(date)) ? dayjs.unix(Number(date) / 1000) : dayjs(date, formatToUse);
        const onChangeDateNow = dateToReturn.hour(now.hour()).minute(now.minute()).second(now.second());
        const isValid = onChangeDateNow?.isValid();

        // Add validation for maxDate and minDate
        return isValid && maxDate && minDate && onChangeDateNow.isAfter(maxDate) && onChangeDateNow.isBefore(minDate)
          ? { onChangeDateNow: null, isValid: false }
          : { onChangeDateNow, isValid };
      },
      [maxDate, minDate, formatToUse]
    );

    const preFireOnChange = useCallback(
      (date: Dayjs | null) => {
        if (!date) return;
        const { isValid } = getOnChangeProps(date);
        if (!isValid && onInvalidInput) {
          onInvalidInput(inputValueRef.current, "invalid date");
        }
      },
      [getOnChangeProps, onInvalidInput]
    );

    const fireOnChange = useCallback(
      (date: Dayjs | null) => {
        if (date === null) return onChange(null, inputValueRef.current, true);
        if (!date) return;
        const { onChangeDateNow, isValid } = getOnChangeProps(date);
        onChange(onChangeDateNow, inputValueRef.current, !!isValid);
      },
      [getOnChangeProps, onChange]
    );

    const shouldDisableDate = useCallback(
      (date: Dayjs) => {
        if (maxDate && date.isAfter(maxDate)) {
          return true;
        }
        return !!(minDate && date.isBefore(minDate));
      },
      [maxDate, minDate]
    );

    const inputValueRef = useRef<string>("");
    const isTouchedRef = useRef<boolean | null>(null);

    return (
      <div
        className={classnames(
          styles.datePickerContainer,
          DatePickerContainerClassName,
          globalStyles[`m-${margin}`],
          globalStyles[`mb-${marginBottom}`],
          globalStyles[`mt-${marginTop}`],
          globalStyles[`mr-${marginRight}`],
          globalStyles[`ml-${marginLeft}`],
          globalStyles[`pb-${paddingBottom}`],
          globalStyles[`pt-${paddingTop}`],
          globalStyles[`pr-${paddingRight}`],
          globalStyles[`pl-${paddingLeft}`],
          globalStyles[`p-${padding}`],
          globalStyles[`pi-${paddingInline}`]
        )}
      >
        <MuiDatePicker<Dayjs>
          PopperProps={{
            placement: "bottom",
            className: classnames({ [globalStyles["darkMode"]]: darkMode, [styles.calendarPopperMiddle]: openCalendarInTheMiddle }),
            sx: { zIndex: 10001 },
            ...({ "data-calendar-popper": "true" } as object),
          }}
          disabled={disabled}
          views={["year", "month", "day"]}
          value={valueToUse}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          minDate={minDate ? dayjs(minDate) : undefined}
          shouldDisableDate={shouldDisableDate}
          disableOpenPicker={hideCalendarIcon}
          renderDay={(days, selectedDate, pickerProps) => {
            const { day } = pickerProps;
            const isDisabled = shouldDisableDate(day);
            return (
              <PickersDay
                day={day}
                {...omit(pickerProps, "day")}
                disabled={isDisabled}
                className={classnames(globalStyles["mui-calendar-day-color"], globalStyles["mui-calendar-day-bg-color"], {
                  [globalStyles["mui-calendar-today"]]: pickerProps.today,
                  [globalStyles["mui-calendar-selected"]]: pickerProps.selected,
                  [globalStyles["mui-out-of-month"]]: pickerProps.outsideCurrentMonth,
                  [globalStyles["mui-calendar-disabled"]]: isDisabled,
                })}
              >
                <p>{day.format("D")}</p>
              </PickersDay>
            );
          }}
          components={{
            OpenPickerIcon: () => (
              <div ref={calendarIconRef}>
                <Svg iconName="calendar" height={16} width={16} className={globalStyles["color-main-primary"]} />
              </div>
            ),
          }}
          OpenPickerButtonProps={{
            className: classnames(styles.OpenPickerButton, globalStyles["hover-bg-color-neutral-secondary"]),
          }}
          PaperProps={{
            className: classnames(
              styles.calanderContainer,
              globalStyles["bg-color-main-secondary"],
              globalStyles["bc-neutral-secondary"],
              globalStyles["color-main-primary"],
              globalStyles.datePickerContainer
            ),
          }}
          showDaysOutsideCurrentMonth
          componentsProps={{
            leftArrowButton: {
              className: globalStyles["color-neutral-primary"],
            },
            rightArrowButton: {
              className: globalStyles["color-neutral-primary"],
            },
            switchViewButton: {
              className: globalStyles["color-neutral-primary"],
            },
          }}
          InputProps={{
            className: classnames(
              globalStyles["color-main-primary"],
              globalStyles[`bc-${borderColor ?? "neutral-secondary"}`],
              globalStyles[`bg-color-${backgroundColor}`],
              globalStyles["v2ElementContainer"],
              styles.inputStyle
            ),
            name,
            componentsProps: {
              input: { "data-testid": dataTestId } as any,
            },
            onChange: (e) => {
              inputValueRef.current = e.target.value;
              if (e.target.value === "") return fireOnChange(null);
              preFireOnChange(valueToUse);
            },
            onClick: () => !hideCalendarIcon && calendarIconRef.current?.click(),
            onBlur: (e) => {
              if (onTouched && !isTouchedRef.current) {
                onTouched();
                isTouchedRef.current = true;
              }
              fireOnChange(dayjs(e.target.value, formatToUse));
            },
          }}
          onOpen={() => onOpenChange?.(true)}
          onChange={(date, inputValue) => {
            if (inputValue === undefined && !isTouchedRef.current) {
              return;
            }
            preFireOnChange(date);
          }}
          onAccept={(date) => {
            preFireOnChange(date);
            fireOnChange(date);
          }}
          onClose={() => {
            if (onTouched && !isTouchedRef.current) {
              onTouched();
              isTouchedRef.current = true;
            }
            onOpenChange?.(false);
          }}
          inputFormat={formatToUse}
          renderInput={({ ...params }) => {
            return <TextField {...params} />;
          }}
          className={classnames(styles.label, globalStyles[`color-main-primary`], className)}
        />
      </div>
    );
  }
);
